import React, { useEffect, useRef, useState } from 'react';
import { DemoWatermarkMarker } from '../data/demoAudioMetadata';
import './AudioWaveformCard.css';

interface AudioWaveformCardProps {
  title: string;
  description: string;
  src: string;
  type: string;
  variant?: 'plain' | 'watermarked';
  watermarkMarkers?: DemoWatermarkMarker[];
}

const formatTime = (seconds: number) => {
  if (!Number.isFinite(seconds)) {
    return '0:00';
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60).toString().padStart(2, '0');
  return `${minutes}:${remainingSeconds}`;
};

const createPeaks = (audioBuffer: AudioBuffer, peakCount: number) => {
  const channelData = audioBuffer.getChannelData(0);
  const samplesPerPeak = Math.max(1, Math.floor(channelData.length / peakCount));
  const peaks: number[] = [];

  for (let peakIndex = 0; peakIndex < peakCount; peakIndex += 1) {
    const start = peakIndex * samplesPerPeak;
    const end = Math.min(start + samplesPerPeak, channelData.length);
    let sum = 0;

    for (let sampleIndex = start; sampleIndex < end; sampleIndex += 1) {
      sum += channelData[sampleIndex] * channelData[sampleIndex];
    }

    peaks.push(Math.sqrt(sum / Math.max(1, end - start)));
  }

  const maxPeak = Math.max(...peaks, 0.001);
  return peaks.map((peak) => peak / maxPeak);
};

const drawWaveform = (
  canvas: HTMLCanvasElement,
  peaks: number[],
  progress: number,
  variant: 'plain' | 'watermarked'
) => {
  const rect = canvas.getBoundingClientRect();
  const pixelRatio = window.devicePixelRatio || 1;
  const width = Math.max(1, Math.floor(rect.width * pixelRatio));
  const height = Math.max(1, Math.floor(rect.height * pixelRatio));
  const context = canvas.getContext('2d');

  if (!context) {
    return;
  }

  if (canvas.width !== width || canvas.height !== height) {
    canvas.width = width;
    canvas.height = height;
  }

  context.clearRect(0, 0, width, height);

  const barGap = 3 * pixelRatio;
  const barWidth = Math.max(2 * pixelRatio, width / peaks.length - barGap);
  const centerY = height / 2;
  const progressX = width * progress;

  peaks.forEach((peak, index) => {
    const x = index * (barWidth + barGap);
    const barHeight = Math.max(4 * pixelRatio, peak * height * 0.82);
    const y = centerY - barHeight / 2;
    const isPlayed = x < progressX;

    const gradient = context.createLinearGradient(0, y, 0, y + barHeight);
    if (variant === 'watermarked') {
      gradient.addColorStop(0, isPlayed ? '#ffbec9' : 'rgba(255, 190, 201, 0.54)');
      gradient.addColorStop(0.58, isPlayed ? '#aba7ed' : 'rgba(171, 167, 237, 0.42)');
      gradient.addColorStop(1, isPlayed ? '#3aa4ea' : 'rgba(58, 164, 234, 0.36)');
    } else {
      gradient.addColorStop(0, isPlayed ? '#dbe3f3' : 'rgba(219, 227, 243, 0.42)');
      gradient.addColorStop(1, isPlayed ? '#8fa3c4' : 'rgba(143, 163, 196, 0.28)');
    }

    context.fillStyle = gradient;
    context.beginPath();
    context.roundRect(x, y, barWidth, barHeight, barWidth / 2);
    context.fill();
  });
};

const AudioWaveformCard = ({
  title,
  description,
  src,
  type,
  variant = 'plain',
  watermarkMarkers = []
}: AudioWaveformCardProps) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const [peaks, setPeaks] = useState<number[]>([]);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [decodeError, setDecodeError] = useState('');

  const progress = duration > 0 ? Math.min(currentTime / duration, 1) : 0;
  const markerPositions = watermarkMarkers
    .map((marker) => ({
      ...marker,
      position: duration > 0 ? Math.min(Math.max(marker.offsetSeconds / duration, 0), 1) : 0
    }))
    .filter((marker) => marker.position >= 0 && marker.position <= 1);

  useEffect(() => {
    let isMounted = true;

    const decodeAudio = async () => {
      setIsLoading(true);
      setDecodeError('');

      try {
        const response = await fetch(src);
        const arrayBuffer = await response.arrayBuffer();
        const AudioContextConstructor =
          window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
        const audioContext = new AudioContextConstructor();
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer.slice(0));
        await audioContext.close();

        if (isMounted) {
          setPeaks(createPeaks(audioBuffer, 96));
          setDuration(audioBuffer.duration);
          setIsLoading(false);
        }
      } catch (error) {
        if (isMounted) {
          setDecodeError('Waveform unavailable');
          setIsLoading(false);
        }
      }
    };

    decodeAudio();

    return () => {
      isMounted = false;
    };
  }, [src]);

  useEffect(() => {
    const canvas = canvasRef.current;

    if (!canvas || peaks.length === 0) {
      return undefined;
    }

    const draw = () => drawWaveform(canvas, peaks, progress, variant);
    draw();
    window.addEventListener('resize', draw);

    return () => {
      window.removeEventListener('resize', draw);
    };
  }, [peaks, progress, variant]);

  useEffect(() => {
    const audio = audioRef.current;

    if (!audio) {
      return undefined;
    }

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration || duration);
    const animate = () => {
      updateTime();
      animationRef.current = window.requestAnimationFrame(animate);
    };
    const startAnimation = () => {
      window.cancelAnimationFrame(animationRef.current || 0);
      animationRef.current = window.requestAnimationFrame(animate);
    };
    const stopAnimation = () => {
      updateTime();
      window.cancelAnimationFrame(animationRef.current || 0);
    };

    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('play', startAnimation);
    audio.addEventListener('pause', stopAnimation);
    audio.addEventListener('ended', stopAnimation);

    return () => {
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('play', startAnimation);
      audio.removeEventListener('pause', stopAnimation);
      audio.removeEventListener('ended', stopAnimation);
      window.cancelAnimationFrame(animationRef.current || 0);
    };
  }, [duration]);

  const handleSeek = (event: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;

    if (!audio || duration <= 0) {
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const nextProgress = Math.min(Math.max((event.clientX - rect.left) / rect.width, 0), 1);
    audio.currentTime = nextProgress * duration;
    setCurrentTime(audio.currentTime);
  };

  return (
    <div className={`waveform-card waveform-card-${variant}`}>
      <div className="waveform-card-header">
        <div>
          <span className="waveform-label">{variant === 'watermarked' ? 'Watermarked signal' : 'Original source'}</span>
          <h3>{title}</h3>
        </div>
        <span className="waveform-time">
          {formatTime(currentTime)} / {formatTime(duration)}
        </span>
      </div>
      <p>{description}</p>
      <div className="waveform-stage" onClick={handleSeek} role="button" tabIndex={0} aria-label={`Seek ${title}`}>
        {isLoading && <span className="waveform-loading">Loading waveform</span>}
        {decodeError && <span className="waveform-loading">{decodeError}</span>}
        <canvas ref={canvasRef} className="waveform-canvas" />
        {markerPositions.map((marker, index) => (
          <span
            className={`watermark-tick ${currentTime >= marker.offsetSeconds ? 'watermark-tick-active' : ''}`}
            style={{ left: `${marker.position * 100}%` }}
            key={marker.id}
            title={`Watermark ${index + 1} at ${formatTime(marker.offsetSeconds)}`}
          >
            <span>{formatTime(marker.offsetSeconds)}</span>
          </span>
        ))}
        <span className="waveform-playhead" style={{ left: `${progress * 100}%` }} />
      </div>
      <audio ref={audioRef} controls className="waveform-audio">
        <source src={src} type={type} />
        Your browser does not support the audio element.
      </audio>
    </div>
  );
};

export default AudioWaveformCard;
